import './logbox';
import * as Notifications from 'expo-notifications';
import { AnyGame, FootballGame, GameStatus, GameTimelineEvent, NotifEventType, Sport, UserPreferences } from '@/types';
import { useFavoritesStore } from '@/store/favorites';
import { notificationsStore } from '@/store/notifications';
import { get365FootballGameDetail, get365FootballGames } from './api/scores365';
import { getNBATodayGames } from './api/nba';
import { getNFLTodayGames } from './api/nfl';

const REMOTE_PUSH_ENABLED = Boolean(process.env.EXPO_PUBLIC_API_BASE);

type PrefKey =
  | 'notifyGameStart'
  | 'notifyGoals'
  | 'notifyYellowCards'
  | 'notifyRedCards'
  | 'notifySubstitutions'
  | 'notifyHalftime'
  | 'notifySecondHalf'
  | 'notifyGameEnd'
  | 'notifyScoreChange';

interface NotifDescriptor {
  emoji: string;
  title: string;
  body: string;
  channelId: 'live-events' | 'game-start';
  eventType: NotifEventType;
  prefKey: PrefKey;
}

interface GameState {
  score: { home: number; away: number };
  status: GameStatus;
  seenEventKeys: Set<string>;
}

function getPrefs(): UserPreferences {
  return useFavoritesStore.getState().preferences;
}

function isLiveGame(game: AnyGame): boolean {
  return game.status === 'live' || game.status === 'halftime';
}

function shortTeamNames(game: AnyGame): { home: string; away: string } {
  return {
    home: game.homeTeam.shortName || game.homeTeam.name,
    away: game.awayTeam.shortName || game.awayTeam.name,
  };
}

function scoreText(game: AnyGame): string {
  return `${game.score.home}-${game.score.away}`;
}

function eventScoreText(event: GameTimelineEvent, game: AnyGame): string {
  if (event.score) return `${event.score.home}-${event.score.away}`;
  return scoreText(game);
}

function minuteText(event: GameTimelineEvent): string {
  return event.minute ?? event.period ?? '';
}

function timelineMinuteOrder(value?: string): number {
  const match = String(value ?? '').replace("'", '').trim().match(/^(\d+)(?:\+(\d+))?/);
  if (!match) return 999;
  return Number(match[1]) + (match[2] ? Number(match[2]) / 100 : 0);
}

function timelineOrder(event: GameTimelineEvent): number {
  if (typeof event.sortOrder === 'number') return event.sortOrder;
  if (event.type === 'game_start') return 0;
  if (event.type === 'halftime' || event.type === 'period_end') return 45.9;
  if (event.type === 'period_start') return 45.95;
  if (event.type === 'game_end') return 130;
  return timelineMinuteOrder(event.minute ?? event.period);
}

function eventKey(event: GameTimelineEvent): string {
  const score = event.score ? `${event.score.home}-${event.score.away}` : '';
  return [
    event.type,
    event.side,
    event.minute ?? '',
    event.period ?? '',
    event.title,
    event.detail ?? '',
    score,
  ].join('|');
}

function currentEventKeys(game: AnyGame): Set<string> {
  return new Set((game.timeline ?? []).map(eventKey));
}

async function pushNotification(
  title: string,
  body: string,
  channelId: 'live-events' | 'game-start',
  data: Record<string, string>
) {
  if (REMOTE_PUSH_ENABLED) return;

  try {
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data,
        sound: true,
        android: { channelId },
      } as any,
      trigger: null,
    });
  } catch (e) {
    console.warn('[LivePoller] Falha ao enviar notificacao:', e);
  }
}

async function emit(
  descriptor: NotifDescriptor,
  gameId: string,
  sport: Sport,
  uniqueSuffix: string
) {
  const prefs = getPrefs();
  if (prefs[descriptor.prefKey] === false) return;

  await pushNotification(descriptor.title, descriptor.body, descriptor.channelId, {
    gameId,
    sport,
    eventType: descriptor.eventType,
  });

  notificationsStore.add({
    id: `${gameId}:${descriptor.eventType}:${uniqueSuffix}`,
    ts: Date.now(),
    emoji: descriptor.emoji,
    title: descriptor.title,
    body: descriptor.body,
    gameId,
    sport,
    eventType: descriptor.eventType,
  });
}

function describeTimelineEvent(event: GameTimelineEvent, game: AnyGame): NotifDescriptor | null {
  const { home, away } = shortTeamNames(game);
  const minute = minuteText(event);
  const minutePrefix = minute ? `${minute} | ` : '';
  const score = eventScoreText(event, game);
  const sideName = event.side === 'home'
    ? game.homeTeam.name
    : event.side === 'away'
      ? game.awayTeam.name
      : '';
  const detail = event.detail ? ` - ${event.detail}` : '';
  const title = event.title.toLowerCase();

  if (event.type === 'game_start') {
    return {
      emoji: '🎮',
      title: 'Jogo começou',
      body: `${home} x ${away}`,
      channelId: 'game-start',
      eventType: 'game_start',
      prefKey: 'notifyGameStart',
    };
  }

  if (event.type === 'halftime' || event.type === 'period_end') {
    return {
      emoji: '⏸️',
      title: 'Fim do primeiro tempo',
      body: `${home} ${score} ${away}`,
      channelId: 'live-events',
      eventType: 'halftime',
      prefKey: 'notifyHalftime',
    };
  }

  if (event.type === 'period_start' && game.sport === 'futebol') {
    return {
      emoji: '▶️',
      title: 'Começou o segundo tempo',
      body: `${home} ${score} ${away}`,
      channelId: 'live-events',
      eventType: 'second_half',
      prefKey: 'notifySecondHalf',
    };
  }

  if (event.type === 'game_end') {
    return {
      emoji: '🏁',
      title: 'Fim de jogo',
      body: `${home} ${score} ${away}`,
      channelId: 'live-events',
      eventType: 'game_end',
      prefKey: 'notifyGameEnd',
    };
  }

  if (event.type === 'goal') {
    return {
      emoji: '⚽',
      title: `Gol - ${sideName}`,
      body: `${minutePrefix}${home} ${score} ${away}${detail}`,
      channelId: 'live-events',
      eventType: 'goal',
      prefKey: 'notifyGoals',
    };
  }

  if (event.type === 'card') {
    const isRed = title.includes('vermelho') || title.includes('red');
    return {
      emoji: isRed ? '🟥' : '🟨',
      title: `${isRed ? 'Cartão vermelho' : 'Cartão amarelo'} - ${sideName}`,
      body: `${minutePrefix}${event.detail ?? event.title}`,
      channelId: 'live-events',
      eventType: isRed ? 'red_card' : 'yellow_card',
      prefKey: isRed ? 'notifyRedCards' : 'notifyYellowCards',
    };
  }

  if (event.type === 'substitution') {
    return {
      emoji: '🔁',
      title: `Substituição - ${sideName}`,
      body: `${minutePrefix}${event.detail ?? event.title}`,
      channelId: 'live-events',
      eventType: 'substitution',
      prefKey: 'notifySubstitutions',
    };
  }

  if (event.type === 'touchdown' || event.type === 'field_goal' || event.type === 'extra_point' || event.type === 'safety' || event.type === 'score') {
    return {
      emoji: game.sport === 'nba' ? '🏀' : '🏈',
      title: event.title,
      body: `${minutePrefix}${home} ${score} ${away}${detail}`,
      channelId: 'live-events',
      eventType: 'score_change',
      prefKey: 'notifyScoreChange',
    };
  }

  return null;
}

class LivePoller {
  private interval: ReturnType<typeof setInterval> | null = null;
  private gameStates = new Map<string, GameState>();
  private pollMs: number;

  constructor(pollMs = 15_000) {
    this.pollMs = pollMs;
  }

  start() {
    if (this.interval) return;
    console.log('[LivePoller] Iniciado - intervalo', this.pollMs / 1000, 's');
    this.poll();
    this.interval = setInterval(() => this.poll(), this.pollMs);
  }

  stop() {
    if (!this.interval) return;
    clearInterval(this.interval);
    this.interval = null;
    console.log('[LivePoller] Parado');
  }

  get running() {
    return this.interval !== null;
  }

  async notifyGameUpdates(games: AnyGame[]) {
    for (const game of games) {
      await this.processGame(game);
    }
  }

  private async poll() {
    try {
      const [footballGames, nbaGames, nflGames] = await Promise.all([
        this.getFootballLiveDetails(),
        getNBATodayGames().catch(() => []),
        getNFLTodayGames().catch(() => []),
      ]);

      await this.notifyGameUpdates([
        ...footballGames,
        ...nbaGames.filter(isLiveGame),
        ...nflGames.filter(isLiveGame),
      ]);
    } catch (err) {
      console.warn('[LivePoller] Erro no poll:', err);
    }
  }

  private async getFootballLiveDetails(): Promise<FootballGame[]> {
    const games = await get365FootballGames().catch(() => [] as FootballGame[]);
    const liveGames = games.filter(isLiveGame).slice(0, 32);
    const details = await Promise.all(
      liveGames.map(async (game) => {
        try {
          return await get365FootballGameDetail(game.id);
        } catch {
          return null;
        }
      })
    );

    return details
      .map((detail, index) => detail ?? liveGames[index])
      .filter((game): game is FootballGame => !!game);
  }

  private async processGame(game: AnyGame) {
    const key = `${game.sport}:${game.id}`;
    const prev = this.gameStates.get(key);
    const eventKeys = currentEventKeys(game);

    if (!prev) {
      this.gameStates.set(key, {
        score: { home: game.score.home, away: game.score.away },
        status: game.status,
        seenEventKeys: eventKeys,
      });
      return;
    }

    const timeline = game.timeline ?? [];
    const hasTimeline = timeline.length > 0;

    if (hasTimeline) {
      const newEvents = timeline
        .filter((event) => !prev.seenEventKeys.has(eventKey(event)))
        .sort((a, b) => timelineOrder(a) - timelineOrder(b));

      for (const event of newEvents) {
        const descriptor = describeTimelineEvent(event, game);
        if (descriptor) {
          await emit(descriptor, game.id, game.sport, eventKey(event));
        }
      }
    }

    const { home, away } = shortTeamNames(game);
    const sc = scoreText(game);

    if (!hasTimeline || game.sport !== 'futebol') {
      if (prev.status !== game.status) {
        if (game.status === 'live' && prev.status === 'scheduled') {
          await emit(
            { emoji: '🎮', title: 'Jogo começou', body: `${home} x ${away}`, channelId: 'game-start', eventType: 'game_start', prefKey: 'notifyGameStart' },
            game.id,
            game.sport,
            'start'
          );
        } else if (game.status === 'finished' && (prev.status === 'live' || prev.status === 'halftime')) {
          await emit(
            { emoji: '🏁', title: 'Fim de jogo', body: `${home} ${sc} ${away}`, channelId: 'live-events', eventType: 'game_end', prefKey: 'notifyGameEnd' },
            game.id,
            game.sport,
            'end'
          );
        }
      }

      if (
        game.status === 'live' &&
        (game.score.home !== prev.score.home || game.score.away !== prev.score.away)
      ) {
        const emoji = game.sport === 'nba' ? '🏀' : game.sport === 'nfl' ? '🏈' : '⚽';
        const label = game.sport === 'nba' ? 'Cesta' : game.sport === 'nfl' ? 'Pontos' : 'Gol';
        await emit(
          { emoji, title: label, body: `${home} ${sc} ${away}`, channelId: 'live-events', eventType: game.sport === 'futebol' ? 'goal' : 'score_change', prefKey: game.sport === 'futebol' ? 'notifyGoals' : 'notifyScoreChange' },
          game.id,
          game.sport,
          `score-${game.score.home}-${game.score.away}`
        );
      }
    }

    this.gameStates.set(key, {
      score: { home: game.score.home, away: game.score.away },
      status: game.status,
      seenEventKeys: new Set([...prev.seenEventKeys, ...eventKeys]),
    });
  }
}

export const livePoller = new LivePoller(15_000);
